import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Building2,
  Users,
  Briefcase,
  MessageSquare,
  Calendar,
  Wrench,
  TrendingUp,
  Home,
  RefreshCw,
  ChevronRight,
  Mail,
  Phone,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const BranchManagerDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branch/dashboard`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load branch dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatK = (n) => `K ${(n || 0).toLocaleString()}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        No branch data available. Please contact your General Manager.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-manager-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{data.branch?.name || "My Branch"}</h1>
          <p className="text-muted-foreground text-sm">
            {data.branch?.location || "—"} · Branch Manager: {user?.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Link to="/dashboard/branch/report">
            <Button data-testid="generate-report-btn">
              <TrendingUp className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={<Users className="w-5 h-5" />}
          label="Team"
          value={data.team_size}
          subtext="Agents in branch"
          color="bg-blue-500"
          link="/dashboard/branch/agents"
        />
        <KpiCard
          icon={<Building2 className="w-5 h-5" />}
          label="Properties"
          value={data.properties.total}
          subtext={`${data.properties.active} active`}
          color="bg-emerald-500"
          link="/dashboard/admin/all-properties"
        />
        <KpiCard
          icon={<Home className="w-5 h-5" />}
          label="Active Rentals"
          value={data.rentals.active}
          subtext={`${data.maintenance_open} maintenance open`}
          color="bg-purple-500"
          link="/dashboard/admin/rentals"
        />
        <KpiCard
          icon={<Briefcase className="w-5 h-5" />}
          label="Revenue"
          value={formatK(data.deals.total_revenue)}
          subtext={`${data.deals.won} deals won`}
          color="bg-amber-500"
          link="/dashboard/admin/all-deals"
        />
      </div>

      {/* Activity row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Open Deals" value={data.deals.open} pipeline={formatK(data.deals.pipeline_value)} />
        <Stat label="Pending Inquiries" value={data.inquiries.pending} pipeline={`of ${data.inquiries.total}`} />
        <Stat label="Upcoming Tours" value={data.tours_upcoming} />
        <Stat label="Open Maintenance" value={data.maintenance_open} />
      </div>

      {/* Monthly revenue chart */}
      {data.monthly_trend && data.monthly_trend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revenue (Last 6 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `K${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatK(v)} />
                <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Team list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>My Team</CardTitle>
          <Link to="/dashboard/branch/agents">
            <Button variant="ghost" size="sm">
              Manage
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {data.agents?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No agents assigned yet. Click "Manage" to assign agents to your branch.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.agents.map((a) => (
                <div
                  key={a.user_id}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                  data-testid={`team-agent-${a.user_id}`}
                >
                  <img
                    src={a.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(a.name)}&size=40`}
                    alt={a.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{a.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Branch contact */}
      <Card>
        <CardHeader>
          <CardTitle>Branch Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span>{data.branch.location || "—"}</span>
          </div>
          {data.branch.phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-muted-foreground" />
              <span>{data.branch.phone}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KpiCard = ({ icon, label, value, subtext, color, link }) => {
  const card = (
    <Card className={link ? "cursor-pointer hover:shadow-md transition-shadow" : ""}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-3">
          <div className={`${color} text-white p-2 rounded-lg`}>{icon}</div>
          <div>
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-sm text-muted-foreground">{label}</div>
            {subtext && <div className="text-xs text-muted-foreground">{subtext}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return link ? <Link to={link}>{card}</Link> : card;
};

const Stat = ({ label, value, pipeline }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
      {pipeline && <div className="text-xs text-muted-foreground">{pipeline}</div>}
    </CardContent>
  </Card>
);

export default BranchManagerDashboard;
